/**
 * §9.9 — the drill-down: the posted lines behind one figure on a report.
 *
 * Opened from an account row on the income statement, the balance sheet or the
 * trial balance, with the window that statement used. The API filters exactly
 * as the statements do, so the Net at the bottom IS the figure that was clicked;
 * `test/db/report-drilldown.test.ts` asserts it for every account.
 *
 * SIGNS. The API speaks debit minus credit. The Balance column and the Net are
 * shown in the account's NORMAL sign, as the statement showed the figure --
 * revenue positive, a liability owed positive. Debit and Credit are shown as
 * they are, because that is what the entry says.
 *
 * Each row opens its entry in the editor, which is where a wrong line is fixed.
 *
 * THE ACCOUNT REGISTER (2026-09-14). Opened from the Accounts screen with no
 * dates at all: every posted line, which is what that screen's balance counts,
 * future-dated entries included. Back then returns to Accounts.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatCents } from '../lib/money.js';
import { ApiError, api, type AccountLinesReport } from '../api.js';

export function AccountLinesPage() {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get('account') ?? '';
  const from = searchParams.get('from') ?? undefined;
  const to = searchParams.get('to') ?? undefined;
  const report = searchParams.get('report') ?? 'income';

  const [data, setData] = useState<AccountLinesReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setError('This link is missing its account.');
      return;
    }
    let cancelled = false;
    setData(null);
    setError(null);
    api
      // Which closing entries the lines leave out (0052), the same as the
      // report they were opened from: every one for the income statement, the
      // one dated on `to` for the trial balance, none for the balance sheet.
      .accountLines(accountId, to, from, {
        closing: report === 'income' ? 'exclude' : report === 'trial' ? 'before' : undefined,
      })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(caught instanceof ApiError ? caught.message : 'Could not read those lines.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, from, to]);

  // Back to where the figure came from: a report's tab and period, or Accounts.
  const backParams = new URLSearchParams({ report });
  if (to) backParams.set('to', to);
  if (from) backParams.set('from', from);
  const back =
    report === 'accounts' ? (
      <Link to="/accounts">Back to Accounts</Link>
    ) : (
      <Link to={`/reports?${backParams.toString()}`}>Back to Reports</Link>
    );

  if (error) {
    return (
      <section className="reports">
        <p role="alert" className="error">
          {error}
        </p>
        {back}
      </section>
    );
  }
  if (!data) return <p className="muted">Loading…</p>;

  const normal = (cents: number) => (data.account.normalBalance === 'credit' ? -cents : cents);

  return (
    <section className="reports account-lines">
      <header className="row reports-head">
        <h1>
          {data.account.code} {data.account.name}
        </h1>
        {back}
      </header>
      <p className="muted">
        {data.from
          ? `${data.from} to ${data.to}`
          : data.to
            ? `Everything through ${data.to}`
            : 'Every posted line'}{' '}
        · posted
        entries, by entry date
      </p>

      {data.lines.length === 0 ? (
        <p className="muted">Nothing was posted to this account in this period.</p>
      ) : (
        <table className="lines">
          <thead>
            <tr>
              <th>Date</th>
              <th>Entry</th>
              <th className="num">Debit</th>
              <th className="num">Credit</th>
              <th className="num">Balance</th>
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line) => (
              <tr key={line.lineId}>
                <td className="date-cell">{line.entryDate}</td>
                <td>
                  <Link to={`/journals/${line.headerId}`}>
                    {line.counterparty ?? line.memo ?? 'Entry'}
                  </Link>
                  {line.counterparty && line.memo && <span className="muted"> · {line.memo}</span>}
                </td>
                <td className="num">{line.debitCents ? formatCents(line.debitCents) : ''}</td>
                <td className="num">{line.creditCents ? formatCents(line.creditCents) : ''}</td>
                <td className="num">{formatCents(normal(line.balanceCents))}</td>
              </tr>
            ))}
            <tr className="report-total is-final">
              <td colSpan={2}>Net</td>
              <td className="num">{formatCents(data.debitCents)}</td>
              <td className="num">{formatCents(data.creditCents)}</td>
              <td className="num">{formatCents(normal(data.netCents))}</td>
            </tr>
          </tbody>
        </table>
      )}

      {data.truncated && (
        <p className="notice notice-warn">
          Showing the first {data.lines.length} of {data.lineCount} lines. The Net includes all of
          them.
        </p>
      )}
    </section>
  );
}
